import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api, type LogEntry, type LogSearchParams } from "@/api/client";
import type { LogRecord } from "./types";
import { TIME_RANGE_OPTIONS } from "./types";

const HISTORY_PAGE_SIZE = 200;

function mapEntry(entry: LogEntry, seq: number): LogRecord {
  return {
    id: `${entry.timestamp}-${seq}`,
    ts: entry.timestamp.includes("T") ? entry.timestamp : entry.timestamp.replace(" ", "T") + "Z",
    level: (entry.level || "INFO").toUpperCase(),
    msg: entry.message,
    pod: entry.pod,
    container: entry.container || null,
    namespace: entry.namespace,
    node: entry.node,
    logger: entry.logger,
    correlationId: entry.correlation_id || undefined,
    context: entry.context,
  };
}

export interface UseLogsSearchOptions {
  enabled: boolean;
  namespace?: string;
  pod?: string;
  container?: string;
  levels: string[];
  search: string;
  correlationId: string;
  range: string;
}

/**
 * Queries ClickHouse for historical log records via `/api/logs/search`
 * with infinite pagination. Returns ascending-time records to match the
 * live-tail viewer ordering.
 */
export function useLogsSearch(opts: UseLogsSearchOptions) {
  const fromIso = useMemo(() => {
    const minutes =
      TIME_RANGE_OPTIONS.find((r) => r.value === opts.range)?.minutes ?? 60;
    return new Date(Date.now() - minutes * 60 * 1000).toISOString();
  }, [opts.range]);

  const baseParams: LogSearchParams = useMemo(
    () => ({
      namespace: opts.namespace || undefined,
      pod: opts.pod || undefined,
      level: opts.levels.length > 0 ? opts.levels : undefined,
      search: opts.search || undefined,
      correlationId: opts.correlationId || undefined,
      from: fromIso,
      limit: HISTORY_PAGE_SIZE,
    }),
    [opts.namespace, opts.pod, opts.levels, opts.search, opts.correlationId, fromIso]
  );

  const queryKey = useMemo(() => ["logs-search", baseParams] as const, [baseParams]);

  const query = useInfiniteQuery({
    queryKey,
    enabled: opts.enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.logs.search({ ...baseParams, offset: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, p) => sum + p.entries.length, 0);
      if (loaded >= lastPage.total) return undefined;
      return loaded;
    },
  });

  // Filters (namespace/pod lists) stay a separate lightweight query so the
  // toolbar populates before the first search returns.
  const filtersQuery = useQuery({
    queryKey: ["logs-filters", opts.namespace],
    queryFn: () => api.logs.filters(opts.namespace),
    enabled: opts.enabled,
    staleTime: 60_000,
  });

  const statsQuery = useQuery({
    queryKey: ["logs-stats", opts.namespace, fromIso],
    queryFn: () => api.logs.stats(opts.namespace, fromIso),
    enabled: opts.enabled,
    staleTime: 60_000,
  });

  const records = useMemo(() => {
    if (!query.data) return [] as LogRecord[];
    const all: LogRecord[] = [];
    let seq = 0;
    // Pages come in descending-time order; flatten then reverse for ascending.
    for (const page of query.data.pages) {
      for (const entry of page.entries) {
        all.push(mapEntry(entry, seq++));
      }
    }
    return all.reverse();
  }, [query.data]);

  const total = query.data?.pages[0]?.total ?? 0;

  return {
    records,
    total,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    filters: filtersQuery.data,
    stats: statsQuery.data,
  };
}
